// src/contexts/LabContext.tsx

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  type ReactNode,
} from "react";

import {
  useLabAnalytics,
} from "../hooks/useLabAnalytics";

import {
  useLabCompletedTests,
} from "../hooks/useLabCompletedTests";

import {
  useLabOngoingTests,
} from "../hooks/useLabOngoingTests";

import {
  useLabPatients,
} from "../hooks/useLabPatients";

import {
  useLabRequests,
} from "../hooks/useLabRequests";

import type {
  LabContextType,
} from "../types/lab-context.types";

export type {
  CompleteTestPayload,
  CompletedTest,
  DeliveryLog,
  DeliveryMethod,
  DeliveryStatus,
  LabContextType,
  OngoingTest,
  Patient,
  RecipientType,
  ResultStatus,
  TestHistory,
  TestPriority,
  TestRequest,
  TestStatistics,
  TestStatus,
} from "../types/lab-context.types";

interface LabProviderProps {
  children: ReactNode;
}

const LabContext =
  createContext<
    LabContextType |
      undefined
  >(undefined);

export const LabProvider = ({
  children,
}: LabProviderProps) => {
  const completedTestsModule =
    useLabCompletedTests();

  const ongoingTestsModule =
    useLabOngoingTests({
      onTestCompleted:
        completedTestsModule
          .addCompletedTest,
    });

  const requestsModule =
    useLabRequests({
      onRequestAccepted:
        ongoingTestsModule
          .addOngoingTest,

      currentLabScientist:
        "Current User",
    });

  const patientsModule =
    useLabPatients({
      ongoingTests:
        ongoingTestsModule
          .ongoingTests,

      completedTests:
        completedTestsModule
          .completedTests,
    });

  const analyticsModule =
    useLabAnalytics({
      testRequests:
        requestsModule
          .testRequests,

      ongoingTests:
        ongoingTestsModule
          .ongoingTests,

      completedTests:
        completedTestsModule
          .completedTests,
    });

  const clearAllData =
    useCallback(
      (): void => {
        requestsModule
          .clearTestRequests();

        ongoingTestsModule
          .clearOngoingTests();

        completedTestsModule
          .clearCompletedTests();

        patientsModule
          .clearPatients();
      },
      [
        completedTestsModule
          .clearCompletedTests,

        ongoingTestsModule
          .clearOngoingTests,

        patientsModule
          .clearPatients,

        requestsModule
          .clearTestRequests,
      ]
    );

  const resetToDefaults =
    useCallback(
      (): void => {
        requestsModule
          .resetTestRequests();

        ongoingTestsModule
          .resetOngoingTests();

        completedTestsModule
          .resetCompletedTests();

        patientsModule
          .resetPatients();
      },
      [
        completedTestsModule
          .resetCompletedTests,

        ongoingTestsModule
          .resetOngoingTests,

        patientsModule
          .resetPatients,

        requestsModule
          .resetTestRequests,
      ]
    );

  const contextValue =
    useMemo<
      LabContextType
    >(
      () => ({
        // Test requests
        testRequests:
          requestsModule
            .testRequests,

        acceptRequest:
          requestsModule
            .acceptRequest,

        rejectRequest:
          requestsModule
            .rejectRequest,

        bulkAcceptRequests:
          requestsModule
            .bulkAcceptRequests,

        bulkRejectRequests:
          requestsModule
            .bulkRejectRequests,

        // Ongoing tests
        ongoingTests:
          ongoingTestsModule
            .ongoingTests,

        updateTestStatus:
          ongoingTestsModule
            .updateTestStatus,

        completeTest:
          ongoingTestsModule
            .completeTest,

        // Completed tests
        completedTests:
          completedTestsModule
            .completedTests,

        updateTestResultStatus:
          completedTestsModule
            .updateTestResultStatus,

        addDeliveryRecord:
          completedTestsModule
            .addDeliveryRecord,

        // Patients
        patients:
          patientsModule
            .patients,

        getPatientTestHistory:
          patientsModule
            .getPatientTestHistory,

        // Analytics
        filterTestsByDateRange:
          analyticsModule
            .filterTestsByDateRange,

        getTestStatistics:
          analyticsModule
            .getTestStatistics,

        getMonthlyTestVolume:
          analyticsModule
            .getMonthlyTestVolume,

        getTestTypeDistribution:
          analyticsModule
            .getTestTypeDistribution,

        getTurnaroundTimeDistribution:
          analyticsModule
            .getTurnaroundTimeDistribution,

        getTopDoctors:
          analyticsModule
            .getTopDoctors,

        getTopHospitals:
          analyticsModule
            .getTopHospitals,

        searchTests:
          analyticsModule
            .searchTests,

        filterByPriority:
          analyticsModule
            .filterByPriority,

        // Development controls
        clearAllData,
        resetToDefaults,
      }),
      [
        analyticsModule
          .filterByPriority,

        analyticsModule
          .filterTestsByDateRange,

        analyticsModule
          .getMonthlyTestVolume,

        analyticsModule
          .getTestStatistics,

        analyticsModule
          .getTestTypeDistribution,

        analyticsModule
          .getTopDoctors,

        analyticsModule
          .getTopHospitals,

        analyticsModule
          .getTurnaroundTimeDistribution,

        analyticsModule
          .searchTests,

        completedTestsModule
          .addDeliveryRecord,

        completedTestsModule
          .completedTests,

        completedTestsModule
          .updateTestResultStatus,

        ongoingTestsModule
          .completeTest,

        ongoingTestsModule
          .ongoingTests,

        ongoingTestsModule
          .updateTestStatus,

        patientsModule
          .getPatientTestHistory,

        patientsModule
          .patients,

        requestsModule
          .acceptRequest,

        requestsModule
          .bulkAcceptRequests,

        requestsModule
          .bulkRejectRequests,

        requestsModule
          .rejectRequest,

        requestsModule
          .testRequests,

        clearAllData,
        resetToDefaults,
      ]
    );

  return (
    <LabContext.Provider
      value={contextValue}
    >
      {children}
    </LabContext.Provider>
  );
};

export const useLab =
  (): LabContextType => {
    const context =
      useContext(
        LabContext
      );

    if (!context) {
      throw new Error(
        "useLab must be used within a LabProvider"
      );
    }

    return context;
  };